import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';

/**
 * Service for detecting device breakpoints in Typescript.
 * Notice that "desktop" does not exist. This app is desktop-first.
 * The max width for desktop is infinite.
 */
@Injectable({ providedIn: 'root' })
export class BreakpointService {
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * max-width breakpoint for mobile devices.
   * compatible with:
   * - `@include tabletPortrait`
   * - `@include tabletLandscape`
   * - `@include desktop`
   */
  static mobile = '768px';

  /**
   * max-width breakpoint for tablet devices in portrait mode
   * compatible with:
   * - `@include mobile`
   * - `@include tabletLandscape`
   * - `@include desktop`
   */
  static tabletPortraitMin = '768.1px';
  static tabletPortraitMax = '1024px';

  /**
   * max-width breakpoint for tablets in landscape mode (implicitly also small desktops)
   * compatible with:
   * - `@include mobile`
   * - `@include tabletPortrait`
   * - `@include desktop`
   */
  static tabletLandscapeMin = '1024.1px';
  static tabletLandscapeMax = '1440px';

  /**
   * min-width breakpoints for desktop devices
   * compatible with:
   * - `@include mobile`
   * - `@include tabletPortrait`
   * - `@include tabletLandscape`
   */
  static desktop = '1441px';

  /**
   * max-width of content within a page that should have left/right margins
   */
  static contentMaxWidth = '1464px';

  /**
   * @returns true if the current screen width represents a mobile device
   */
  get mobile$(): Observable<boolean> {
    return this.breakpointObserver.observe(`(max-width: ${BreakpointService.mobile})`).pipe(
      map(({ matches }) => matches),
      shareReplay(1),
    );
  }

  /**
   * @returns true if the current screen width represents a tablet device
   */
  get tabletPortrait$(): Observable<boolean> {
    return this.breakpointObserver
      .observe(
        `(min-width: ${BreakpointService.tabletPortraitMin}) and (max-width: ${BreakpointService.tabletPortraitMax})`,
      )
      .pipe(
        map(({ matches }) => matches),
        shareReplay(1),
      );
  }

  /**
   * @returns true if the current screen width represents a desktop device
   */
  get tabletLandscape$(): Observable<boolean> {
    return this.breakpointObserver
      .observe(
        `(min-width: ${BreakpointService.tabletLandscapeMin}) and (max-width: ${BreakpointService.tabletLandscapeMax})`,
      )
      .pipe(
        map(({ matches }) => matches),
        shareReplay(1),
      );
  }

  /**
   * @returns true if the current screen width represents a desktop device
   *          by checking that {@link mobile$}, {@link tabletPortrait$},
   *          and {@link tabletLandscape$} are all false
   */
  get desktop$(): Observable<boolean> {
    // Cannot use forkJoin here. It requires all Observables to complete before emitting an event.
    // BreakpointObserver.observe is a long-living Observable, so it never completes.
    return combineLatest([this.mobile$, this.tabletPortrait$, this.tabletLandscape$]).pipe(
      map(([isMobile, isTabletPortrait, isTabletLandscape]) => {
        return !isMobile && !isTabletPortrait && !isTabletLandscape;
      }),
      shareReplay(1),
    );
  }
}
